// Mutual authentication bound to DTLS (docs/phase-1-drop.md § Sécurité).
//
// The signaling server relays SDP, so a malicious one could swap the DTLS fingerprints and sit
// in the middle. Each peer therefore proves knowledge of the link secret over the data channel,
// MACing both fingerprints *as it sees them*: under interposition the two sides see different
// fingerprints and the proofs do not verify. An optional password is mixed into the key, so it
// never travels.

import { b64url, fromB64url } from './ids';

const enc = new TextEncoder();

/** First `a=fingerprint:sha-256` of an SDP, normalised to upper case. */
export function sdpFingerprint(sdp: string | undefined): string | null {
  const m = /a=fingerprint:sha-256 ([0-9A-Fa-f:]+)/.exec(sdp ?? '');
  return m ? m[1].toUpperCase() : null;
}

export async function deriveAuthKey(secret: string, password = ''): Promise<CryptoKey> {
  const ikm = new Uint8Array([...enc.encode(secret), 0, ...enc.encode(password)]);
  const base = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('drop-v1'), info: enc.encode('auth') },
    base,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign', 'verify'],
  );
}

const message = (from: string, to: string) => enc.encode(`drop-auth-v1|${from}|${to}`);

/** Proof sent by this side: MAC(local fingerprint → remote fingerprint). */
export async function makeProof(key: CryptoKey, localFp: string, remoteFp: string): Promise<string> {
  return b64url(new Uint8Array(await crypto.subtle.sign('HMAC', key, message(localFp, remoteFp))));
}

/** Checks the remote's proof: it MACed (its local = our remote) → (its remote = our local). */
export async function checkProof(key: CryptoKey, proof: string, localFp: string, remoteFp: string): Promise<boolean> {
  let sig: Uint8Array<ArrayBuffer>;
  try {
    sig = fromB64url(proof);
  } catch {
    return false;
  }
  return crypto.subtle.verify('HMAC', key, sig, message(remoteFp, localFp));
}
